import React from 'react';
import { Link } from 'react-router-dom';
import { ShinyText } from '../ui/shiny-text';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
      {/* Header */}
      <header className="fixed w-full top-0 bg-black/50 backdrop-blur-sm z-50">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-bold">
            <ShinyText>WrapPractice</ShinyText>
          </Link>
          <Link
            to="/"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Content */}
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
            <ShinyText>Privacy Policy</ShinyText>
          </h1>
          
          <div className="bg-gray-900/50 rounded-xl p-8 space-y-8 text-gray-100">
            <div className="text-center text-gray-400 mb-8">
              <p>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">1. Introduction</h2>
              <p className="mb-4">
                Nestsim LLC ("we," "our," or "us") operates the WrapPractice application and website 
                (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, 
                and safeguard your information when you use our Service for practicing Pot Limit Omaha (PLO) wrap identification.
              </p>
              <p>
                By using our Service, you consent to the data practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">2.1 Account Information</h3>
              <p className="mb-4">
                We collect information from third-party authentication providers when you create an account:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Google OAuth:</strong> Email address, name, profile picture, and Google account ID</li>
                <li><strong>Facebook OAuth:</strong> Email address, name, profile picture, and Facebook account ID</li>
                <li><strong>Microsoft OAuth:</strong> Email address, name, profile picture, and Microsoft account ID</li>
              </ul>
              <p className="mb-4">
                We do not store passwords as we rely exclusively on third-party authentication providers.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">2.2 Payment Information</h3>
              <p className="mb-4">
                For subscription payments, we use Stripe as our payment processor:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Stripe Customer ID (stored in our database)</li>
                <li>Subscription status and billing information</li>
                <li>Payment method details (stored securely by Stripe, not on our servers)</li>
                <li>Transaction history and billing records</li>
              </ul>
              <p className="mb-4">
                Credit card numbers and sensitive payment data are processed and stored by Stripe according to 
                PCI DSS standards. We never have access to your full credit card information.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">2.3 Usage Data</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Practice session statistics and performance metrics</li>
                <li>Hand identification accuracy and response times</li>
                <li>Feature usage patterns and app interaction data</li>
                <li>Device information (browser type, operating system, screen resolution)</li>
                <li>IP address and general location information</li>
                <li>Log data including access times and error reports</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">2.4 Communications</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Support ticket conversations and chat transcripts (via Tawk.to integration)</li>
                <li>Email communications regarding your account and service updates</li>
                <li>Feedback and survey responses</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">3. How We Use Your Information</h2>
              <p className="mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account Management:</strong> Creating and maintaining your user account</li>
                <li><strong>Service Provision:</strong> Delivering the poker practice functionality and tracking your progress</li>
                <li><strong>Payment Processing:</strong> Managing subscriptions, trials, and billing through Stripe</li>
                <li><strong>Customer Support:</strong> Responding to inquiries and providing technical assistance</li>
                <li><strong>Service Improvement:</strong> Analyzing usage patterns to enhance features and user experience</li>
                <li><strong>Communication:</strong> Sending service-related notifications and updates</li>
                <li><strong>Legal Compliance:</strong> Meeting regulatory requirements and preventing fraud</li>
                <li><strong>Security:</strong> Monitoring for suspicious activity and maintaining system security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">4. Information Sharing and Disclosure</h2>
              <p className="mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties except as described below:</p>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">4.1 Service Providers</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                <li><strong>Google Cloud Platform:</strong> Hosting, database, and infrastructure services</li>
                <li><strong>Firebase:</strong> Frontend hosting and authentication services</li>
                <li><strong>Tawk.to:</strong> Customer support chat functionality</li>
                <li><strong>Authentication Providers:</strong> Google, Facebook, and Microsoft for account verification</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">4.2 Legal Requirements</h3>
              <p className="mb-4">
                We may disclose your information if required by law, regulation, or legal process, or to protect 
                the rights, property, or safety of Nestsim LLC, our users, or others.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">4.3 Business Transfers</h3>
              <p className="mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                as part of the business transaction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">5. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Data encryption in transit and at rest</li>
                <li>Secure HTTPS connections for all data transmission</li>
                <li>Regular security assessments and updates</li>
                <li>Limited access to personal data on a need-to-know basis</li>
                <li>PCI DSS compliant payment processing through Stripe</li>
                <li>Regular backups with encryption</li>
              </ul>
              <p className="mb-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. 
                While we strive to protect your personal information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">6. Data Retention</h2>
              <p className="mb-4">
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account Information:</strong> Until account deletion is requested</li>
                <li><strong>Payment Data:</strong> As required by tax laws and Stripe's retention policies</li>
                <li><strong>Usage Data:</strong> Up to 2 years for analytics and service improvement</li>
                <li><strong>Support Communications:</strong> Up to 3 years for customer service purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">7. Your Rights and Choices</h2>
              <p className="mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to processing of your personal information for certain purposes</li>
                <li><strong>Restriction:</strong> Request limitation of processing in certain circumstances</li>
              </ul>
              <p className="mb-4">
                To exercise these rights, please contact us using the information provided in Section 10.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">8. Cookies and Tracking Technologies</h2>
              <p className="mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for authentication and basic functionality</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use our service</li>
                <li><strong>Performance Cookies:</strong> Monitor and improve application performance</li>
              </ul>
              <p className="mb-4">
                You can control cookie settings through your browser preferences, though disabling certain 
                cookies may affect functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">9. Children's Privacy</h2>
              <p className="mb-4">
                Our Service is not intended for individuals under the age of 18. We do not knowingly collect 
                personal information from children under 18. If we become aware that we have collected 
                personal information from a child under 18, we will take steps to delete such information promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">10. International Data Transfers</h2>
              <p className="mb-4">
                Your information may be transferred to and processed in countries other than your own, including 
                the United States where our servers are located. We ensure appropriate safeguards are in place 
                to protect your personal information in accordance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">11. Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                Continued use of the Service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">12. Contact Information</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-800 p-4 rounded-lg">
                <p><strong>Nestsim LLC</strong></p>
                <p>Support: <a href="https://embed.tawk.to/68430f9adbadee190ffc2bc0/1it2v9ss4" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Live Chat Support</a></p>
                <p>Website: <Link to="/" className="text-blue-400 hover:text-blue-300">wrappractice.nestsim.com</Link></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black/50 py-8">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <div className="flex items-center justify-center gap-3">
            <img src="/images/NestSim_logo_128px.png" alt="Nestsim LLC Logo" />
            <p>&copy; {new Date().getFullYear()} Nestsim LLC™. All rights reserved.</p>
          </div>
          <div className="mt-4 space-x-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-white transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;